// The navigation redesign (Plan 7, 2026-10-28): the portfolio screens — Processos (list, map, the Ambiental licences
// across the processes), the movements history for Atividades, the extra Cadastro Mineiro data on a process (Dados ANM)
// and the Superficiários register of the Território module. Same rule as product.ts: undeclared keys are dropped.
import { z } from 'zod';
import { Id, IsoDate } from './accounts.ts';
import { AnmEntityFilter, AnmRecord, EnvLicenceView, EnvRequestView, Freshness, Movement } from './product.ts';
import { CarFindingView, ParcelFindingView } from './spatial.ts';

// --- Processos: every process of the visible titulares -----------------------------------------------------------------

/** The next open obligation of a process (the earliest dated; an undated act-now one first). */
export const ProcessNextObligation = z.object({
  obligationId: Id,
  ruleId: z.string(),
  /** The obligation's `basis` (what to do), as ObligationView carries it. */
  basis: z.string(),
  dueOn: IsoDate.nullable(),
  daysLeft: z.number().int().nullable(),
  confidence: z.enum(['high', 'medium', 'low']),
  /** 'anm' or an environmental agency id (`iat_pr`, `ima_sc`, `ibama`). */
  authority: z.string(),
});
export const ProcessListItem = z.object({
  id: Id,
  canonicalRef: z.string().nullable(),
  anmEntityId: Id,
  titular: z.string().nullable(),
  phase: z.string().nullable(),
  uf: z.string().nullable(),
  areaHa: z.number().nullable(),
  active: z.boolean().nullable(),
  /** Current substances (no end date), as ANM names them ("BENTONITA"), with their use. */
  substances: z.array(z.object({ name: z.string(), use: z.string().nullable() })),
  municipalities: z.array(z.string()),
  next: ProcessNextObligation.nullable(),
  /** Open obligations already past due (dated, daysLeft < 0). */
  overdue: z.number().int().nonnegative(),
  openObligations: z.number().int().nonnegative(),
  lastEventOn: IsoDate.nullable(),
  /** Counts the list's module badges need; null when the subscription lacks the module (shown locked). */
  envLicences: z.number().int().nonnegative().nullable(),
});
export type ProcessListItem = z.infer<typeof ProcessListItem>;
export const ProcessListQuery = AnmEntityFilter.extend({
  /** Inactive processes too (default: active only). */
  all: z.enum(['1']).optional(),
});
export const ProcessListResponse = z.object({
  anmEntities: z.array(Id),
  items: z.array(ProcessListItem),
  /** Totals over `items` for the header ("30 ativos · 10.457 ha"). */
  totals: z.object({ active: z.number().int(), inactive: z.number().int(), areaHa: z.number() }),
  freshness: Freshness,
});
export type ProcessListResponse = z.infer<typeof ProcessListResponse>;

/** The polygons of the listed processes for the map view — simplified, lon/lat, 5 decimals. */
export const ProcessShapesQuery = AnmEntityFilter.extend({ all: z.enum(['1']).optional() });
export const ProcessShape = z.object({
  id: Id,
  canonicalRef: z.string().nullable(),
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()])))),
});
export const ProcessShapesResponse = z.object({ shapes: z.array(ProcessShape) });
export type ProcessShape = z.infer<typeof ProcessShape>;
export type ProcessShapesResponse = z.infer<typeof ProcessShapesResponse>;

/** Processos → Ambientais: every agency licence and followed request across the visible titulares (Ambiental). */
export const PortfolioLicence = EnvLicenceView.extend({
  anmEntityId: Id,
  /** The process it is placed on; null = the titular's, on none of its processes ("fora dos processos"). */
  processId: Id.nullable(),
  canonicalRef: z.string().nullable(),
  /** Its open deadline (validity or renewal), when it has one. */
  next: ProcessNextObligation.nullable(),
});
export const PortfolioRequest = EnvRequestView.extend({ processId: Id.nullable(), canonicalRef: z.string().nullable() });
export const PortfolioAmbientalResponse = z.discriminatedUnion('status', [
  z.object({ status: z.literal('locked') }),
  z.object({ status: z.literal('open'), licences: z.array(PortfolioLicence), requests: z.array(PortfolioRequest) }),
]);
export type PortfolioLicence = z.infer<typeof PortfolioLicence>;
export type PortfolioRequest = z.infer<typeof PortfolioRequest>;
export type PortfolioAmbientalResponse = z.infer<typeof PortfolioAmbientalResponse>;

// --- Atividades → Movimentações: the movements history ------------------------------------------------------------------

export const MovementsQuery = AnmEntityFilter.extend({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  processId: Id.optional(),
  /** Event authority: 'anm', 'iat_pr', 'ima_sc', 'ibama'… */
  authority: z.string().regex(/^[a-z_]{2,20}$/).optional(),
  /** Older than this (the previous page's `nextBefore`). */
  before: IsoDate.optional(),
});
export const MovementsResponse = z.object({
  from: IsoDate,
  to: IsoDate,
  movements: z.array(Movement),
  /** Pass as `before` for the next page; null = no more. */
  nextBefore: IsoDate.nullable(),
});
export type MovementsResponse = z.infer<typeof MovementsResponse>;

// --- Processo → Dados ANM: what the Cadastro Mineiro holds beyond the summary -------------------------------------------

export const ProcessAnmData = AnmRecord.extend({
  /** Filings through the Protocolo Digital (newest first). */
  filings: z.array(z.object({ filedOn: IsoDate, subject: z.string().nullable(), nup: z.string().nullable() })),
});
export type ProcessAnmData = z.infer<typeof ProcessAnmData>;
export const ProcessAnmParams = z.object({ processId: Id });

// --- Território → Superficiários ----------------------------------------------------------------------------------------

export const AgreementType = z.enum(['none', 'negotiating', 'access', 'easement', 'lease', 'purchase', 'court']);
export const AgreementPeriod = z.enum(['monthly', 'yearly', 'once', 'royalty']);
/** A landowner and the agreement, registered by the customer — private to its subscription (like a manual licence). */
export const LandOwnerView = z.object({
  id: Id,
  processId: Id.nullable(),
  /** The property it is about: a SIGEF parcel code or a CAR code; null = typed without one. */
  parcelSource: z.enum(['sigef', 'car']).nullable(),
  parcelCode: z.string().nullable(),
  name: z.string(),
  document: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  agreement: AgreementType,
  amountCents: z.number().int().nullable(),
  period: AgreementPeriod.nullable(),
  startsOn: IsoDate.nullable(),
  endsOn: IsoDate.nullable(),
  notes: z.string().nullable(),
  remind: z.boolean(),
  updatedAt: z.string(),
});
export type LandOwnerView = z.infer<typeof LandOwnerView>;
export const LandOwnerBody = z.object({
  processId: Id.nullable(),
  parcelSource: z.enum(['sigef', 'car']).nullable(),
  parcelCode: z.string().trim().min(1).max(80).nullable(),
  name: z.string().trim().min(2).max(160),
  document: z.string().trim().max(20).nullable(),
  phone: z.string().trim().max(30).nullable(),
  email: z.string().trim().email().max(160).nullable(),
  agreement: AgreementType,
  amountCents: z.number().int().nonnegative().max(1e12).nullable(),
  period: AgreementPeriod.nullable(),
  startsOn: IsoDate.nullable(),
  endsOn: IsoDate.nullable(),
  notes: z.string().trim().max(2000).nullable(),
  remind: z.boolean(),
});
export type LandOwnerBody = z.infer<typeof LandOwnerBody>;
export const LandOwnerParams = z.object({ ownerId: Id });
export const LandOwnerResponse = z.object({ id: Id });

/** A property over one of the processes, with the owners this subscription registered for it. */
export const PortfolioParcel = z.object({
  processId: Id,
  canonicalRef: z.string().nullable(),
  parcelSource: z.enum(['sigef', 'car']),
  sigef: ParcelFindingView.nullable(),
  car: CarFindingView.nullable(),
  owners: z.array(LandOwnerView),
});
export type PortfolioParcel = z.infer<typeof PortfolioParcel>;
export const SuperficiariosResponse = z.discriminatedUnion('status', [
  z.object({ status: z.literal('locked') }),
  z.object({
    status: z.literal('open'),
    parcels: z.array(PortfolioParcel),
    /** Owners typed without a property (or whose property no longer overlaps). */
    unattached: z.array(LandOwnerView),
    /** CAR is read from SFB per process; false when the Ambiental module is off (CAR rows hidden) or SFB failed. */
    carIncluded: z.boolean(),
  }),
]);
export type SuperficiariosResponse = z.infer<typeof SuperficiariosResponse>;
